// Activities, ghosts and custom workouts. The server is the source of truth (ADR 0013): every read goes to the API and
// fills the browser cache (IndexedDB) that serves the screens while the network is down. An activity recorded while the
// server is unreachable waits in the outbox (`synced: false`) without rewards, until sync.ts flushes it.
import { toTCX as coreTCX, buildTrack, officialTrackDef, summarize, levelFromXp, type Samples, type Summary, type ActivityMeta, type RewardResult } from '@gruvetto/core';
import { db, type DbRow } from './db.js';
import { state, update } from './store.js';
import { api, ApiError } from './api.js';
import { refreshRatingQuietly } from './rating.js';
import { adoptFtp, pullMe } from './sync.js';
import { LiveRecording, RecordingLost, newActivityId as newId, type SaveResponse, type ServerActivity } from './recordings.js';
import type { FtpState } from '@gruvetto/contracts';
import type { FtpAround } from '../ui/ride/ftp-moment.js';

/** Activity as stored locally: the server row flattened with its metadata. */
export interface LocalActivity extends Partial<Omit<ActivityMeta, 'type' | 'name'>> {
  id: string; date: number; type: string; name: string; summary: Summary; xp: number; ftp: number; mass: number;
  /** False: recorded while the server was unreachable, waiting in the outbox. */
  synced?: boolean;
  /** The server holds a replay of this race (data/replays.ts). */
  replay?: boolean;
  /** Fields added by the server (events, stages…) that screens read ad hoc. */
  [k: string]: unknown;
}
const fromServer = (a: ServerActivity): LocalActivity => ({ ...a.meta, id: a.id, date: a.date, type: a.type, name: a.name, summary: a.summary, xp: a.xp, ftp: a.ftp, mass: a.mass, replay: !!a.replay, synced: true });
const offline = (e: unknown) => e instanceof ApiError && e.offline;
const byDate = (list: LocalActivity[]) => list.sort((a, b) => b.date - a.date);

export async function listActivities(): Promise<LocalActivity[]> {
  const local = (await db.all('activities')) as LocalActivity[];
  const pending = local.filter(a => a.synced === false);
  try {
    const remote = (await api<ServerActivity[]>('/activities')).map(fromServer);
    for (const a of remote) { const l = local.find(x => x.id === a.id); if (!l || l.xp !== a.xp || l.replay !== a.replay) await db.put('activities', a); }
    return byDate([...pending, ...remote]);
  } catch (e) {
    if (!offline(e)) throw e;
    return byDate(local); // the cache, while the server is unreachable
  }
}

export async function getActivity(id: string): Promise<LocalActivity | null> {
  const local = (await db.get('activities', id)) as LocalActivity | undefined;
  if (local) return local;
  const a = await api<ServerActivity>(`/activities/${id}`).catch(() => null);
  if (!a) return null;
  await db.put('activities', fromServer(a)); if (a.samples) await db.put('samples', { id, data: a.samples });
  return fromServer(a);
}
export async function getSamples(id: string): Promise<Samples | null> {
  const s = (await db.get('samples', id))?.data as Samples | undefined;
  if (s) return s;
  const a = await api<ServerActivity>(`/activities/${id}`).catch(() => null);
  if (a?.samples) await db.put('samples', { id, data: a.samples });
  return a?.samples || null;
}
export async function deleteActivity(id: string) {
  const local = (await db.get('activities', id)) as LocalActivity | undefined;
  // an activity still in the outbox never reached the server: dropping it locally is enough
  if (local?.synced !== false) await api(`/activities/${id}`, { method: 'DELETE' });
  await db.del('activities', id); await db.del('samples', id); await db.del('replays', id);
  refreshRatingQuietly();
  // the FTP is estimated from the rides: one fewer may move it
  pullMe().catch(() => {});
}

/** Result handed to the end screen. */
export interface SaveResult {
  activity: LocalActivity; xp: RewardResult['xp']; achievements: RewardResult['achievements']; gained: number; before: RewardResult['before']; after: RewardResult['after']; unlocks: RewardResult['unlocks']; coins?: number; offline?: boolean;
  /** The FTP before and after the ride, as the server estimated it (ADR 0027); absent when the server did not answer. */
  ftp?: FtpAround & { testDue: FtpState['testDue'] };
}

/**
 * Saves a ride. With its live recording (ADR 0019) the server already holds the samples: the close stores the activity;
 * without one, or when the server gave the recording up, the whole recording goes in one request.
 */
export async function saveActivity(meta: ActivityMeta, samples: Samples, live: LiveRecording | null = null): Promise<SaveResult> {
  const r = await (live ? saveLive(meta, samples, live) : saveWhole(newId(), Date.now(), meta, samples));
  // race efforts count in the power profile: the score may move after a ride
  refreshRatingQuietly();
  return r;
}

async function saveLive(meta: ActivityMeta, samples: Samples, live: LiveRecording): Promise<SaveResult> {
  try {
    return await adopt(await live.close(meta), live.id, samples);
  } catch (e) {
    if (e instanceof RecordingLost) return saveWhole(live.id, live.date, meta, samples);
    if (!offline(e)) throw e;
    return toOutbox(live.id, live.date, meta, samples);
  }
}

async function saveWhole(id: string, date: number, meta: ActivityMeta, samples: Samples): Promise<SaveResult> {
  try {
    return await adopt(await api<SaveResponse>('/activities', { method: 'POST', body: { id, date, meta, samples } }), id, samples);
  } catch (e) {
    if (!offline(e)) throw e;
    return toOutbox(id, date, meta, samples);
  }
}

/** Takes the server's answer in: state, caches, result for the end screen. A duplicate (the server settled the ride already) is read back without rewards. */
export async function adopt(r: SaveResponse, id: string, samples: Samples): Promise<SaveResult> {
  // (`in`, not the flag: apps/web is not strict yet, and truthiness does not narrow an optional discriminant there)
  if (!('activity' in r)) {
    const a = await api<ServerActivity>(`/activities/${id}`), L = levelFromXp(state.progress.xp), act = fromServer(a);
    await db.put('activities', act); await db.put('samples', { id, data: a.samples ?? samples });
    return { activity: act, xp: [], achievements: [], gained: a.xp, before: L, after: L, unlocks: [], coins: 0 };
  }
  const ftp = r.ftp ? ftpAround(r.ftp) : null;
  update(s => { s.progress = r.progress; s.totals = r.totals; s.plan = r.plan; });
  if (r.ftp) adoptFtp(r.ftp);
  const act = fromServer(r.activity);
  await db.put('activities', act); await db.put('samples', { id, data: samples });
  return { activity: act, ...r.rewards, ...(ftp ? { ftp } : {}) };
}

/** The FTP before this ride (the local copy of the server's estimate) and after it (the server's answer). */
function ftpAround(after: FtpState): FtpAround & { testDue: FtpState['testDue'] } {
  const before = state.ftp, basisBefore = before?.basis ?? 'seed', from = state.profile.ftp;
  // the best the rides had measured: every value the history reached, and the current one unless it is the weight-based start
  const bestBefore = Math.max(0, ...(before?.history ?? []).map(h => h.ftp), basisBefore === 'seed' ? 0 : from);
  return { from, to: after.ftp, basisBefore, basis: after.basis, bestBefore, testDue: after.testDue };
}

/**
 * Server unreachable: the activity waits in the outbox with a local summary for the lists; XP, achievements and the
 * score are the server's to compute (ADR 0013), the end screen shows none until the next sync.
 */
async function toOutbox(id: string, date: number, meta: ActivityMeta, samples: Samples): Promise<SaveResult> {
  const summary = summarize(samples, state.profile.ftp, state.profile.mass), L = levelFromXp(state.progress.xp);
  const act: LocalActivity = { ...meta, id, date, type: meta.type, name: meta.name, summary, xp: 0, ftp: state.profile.ftp, mass: state.profile.mass, synced: false, meta };
  await db.put('activities', act); await db.put('samples', { id, data: samples });
  return { activity: act, xp: [], achievements: [], gained: 0, before: L, after: L, unlocks: [], coins: 0, offline: true };
}

// ---------- TCX export (importable into Strava, Garmin Connect, intervals.icu…) ----------
/** Same file as the one the server sends to Strava (core builds it). */
export const toTCX = (act: LocalActivity, S: Samples): string => {
  const def = officialTrackDef(act.track);
  return coreTCX(act, S, act.date, def ? buildTrack({ id: act.track!, ...def }) : null);
};

export function download(name: string, text: string, type = 'application/xml') {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

// ---------- Ghosts ----------
export interface Ghost { time: number; rec: [number, number][] }
export async function getGhost(key: string): Promise<Ghost | null | undefined> {
  try {
    const g = await api<Ghost | null>(`/ghosts/${key}`);
    if (g) await db.put('ghosts', { key, ...g });
    return g;
  } catch (e) {
    if (!offline(e)) throw e;
    return db.get<DbRow & Ghost>('ghosts', key);
  }
}
export async function putGhost(key: string, time: number, rec: [number, number][], activityId: string) {
  await db.put('ghosts', { key, time, rec, date: Date.now() });
  await api(`/ghosts/${key}`, { method: 'PUT', body: { activityId, time, rec } }).catch(() => {});
}

// ---------- Custom workouts ----------
export async function listCustomWorkouts() {
  try {
    const w = await api<DbRow[]>('/workouts');
    for (const x of w) await db.put('workouts', x);
    return w;
  } catch (e) {
    if (!offline(e)) throw e;
    return db.all('workouts');
  }
}
export async function saveCustomWorkout(w: DbRow & { id: string }) {
  const data = { ...w, custom: true };
  await api(`/workouts/${encodeURIComponent(w.id)}`, { method: 'PUT', body: data });
  await db.put('workouts', data);
}
export async function deleteCustomWorkout(id: string) {
  await api(`/workouts/${encodeURIComponent(id)}`, { method: 'DELETE' });
  await db.del('workouts', id);
}
